import { Dialog, Transition } from '@headlessui/react'
import { CurrencyDollarIcon } from '@heroicons/react/24/outline'
import { API, Auth } from 'aws-amplify'
import React, { Fragment, useState } from 'react'
import ResultModal from '../../components/ResultModal'
import currencies from '../../config/currency'
import i18n from '../../i18n'
import Payment from './Payment'

const modalMsg = {
  ok: {
    title: 'Operación realizada',
    content: 'El cambio en el saldo del cliente se ha realizado corectamente',
    cta: 'Ok'
  },
  error: {
    title: 'Error',
    content:
      'No se ha podido realizar la operación, por favor revise los datos introducidos y si el error persiste contacte con support@tadoo.biz para más información',
    cta: 'Error'
  }
}

const AddMoneyModal = ({
  setTopUps,
  client,
  open,
  setOpen,
  userInfo,
  onTopUpSuccess,
  users = [],
  selectedUserId,
  setSelectedUserId
}) => {
  const defaultErrors = {
    invalidAmountFormat: true,
    minimumAmountError: false,
    decimalsInvalid: false
  }

  const [amount, setAmount] = useState('')
  const [errors, setErrors] = useState(defaultErrors)
  const [paymentView, setPaymentView] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(
    userInfo?.isAdmin ? 'manual' : 'stripe'
  )
  const [concept, setConcept] = useState('')
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split('T')[0]
  )
  const [isLoading, setIsLoading] = useState(false)
  const [showResult, setShowResult] = useState(false)
  const [resultContent, setResultContent] = useState(null)
  const currency = client?.currency ? client.currency : 'EUR'

  const errorText = {
    invalidAmountFormat: i18n.t('error_amount_format'),
    minimumAmountError: i18n.t('error_minimum_amount', {
      minimum: currencies[currency].minimum
    }),
    decimalsInvalid: i18n.t('error_amount_decimals', { currency: currency })
  }

  const [existErrorsResult, setExistErrorsResult] = useState(true)

  const checkAmount = (newAmount, oldErrors) => {
    let newErrors = oldErrors
    if (currency === 'HUF') {
      if (newAmount % 100 !== 0) {
        newErrors.decimalsInvalid = true

        setExistErrorsResult(true)
      } else {
        newErrors.decimalsInvalid = false
      }
    }
    return newErrors
  }

  const checkNumber = (event) => {
    let newErrors = errors
    if (/^\d+(?:\.\d{1,2})?$/.test(event.target.value)) {
      //check minimum
      if (parseFloat(event.target.value) < currencies[currency].minimum) {
        newErrors.invalidAmountFormat = false
        newErrors.minimumAmountError = true

        setExistErrorsResult(true)
      } else {
        newErrors.invalidAmountFormat = false
        newErrors.minimumAmountError = false

        setExistErrorsResult(false)
      }
      //check currency format
      newErrors = checkAmount(parseFloat(event.target.value) * 100, newErrors)
    } else {
      newErrors.invalidAmountFormat = true
      newErrors.minimumAmountError = false
      newErrors.decimalsInvalid = false
      setExistErrorsResult(true)
    }
    setErrors(newErrors)
    setAmount(parseFloat(event.target.value) * 100)
  }

  const topUp = async () => {
    if (userInfo?.isAdmin && !selectedUserId) {
      return
    }
    if (paymentMethod === 'manual' && userInfo?.isAdmin) {
      // Top up manual para admin
      setIsLoading(true)
      setShowResult(false)
      setResultContent(null)
      try {
        const tokenReq = (await Auth.currentAuthenticatedUser())
          .signInUserSession.idToken.jwtToken
        await API.post('rcs-api', '/topups', {
          headers: {
            Authorization: tokenReq
          },
          body: {
            ...client,
            topUpAmount: parseFloat(amount / 100),
            concept: concept || 'Manual top up',
            paymentDate: paymentDate,
            currency: currency
          }
        })
        // Reset form
        setAmount('')
        setConcept('')
        setPaymentDate(new Date().toISOString().split('T')[0])
        setPaymentView(false)
        setOpen(false)
        setPaymentMethod(userInfo?.isAdmin ? 'manual' : 'stripe')
        // Llamar callback para refrescar top ups
        if (onTopUpSuccess) {
          onTopUpSuccess()
        }
      } catch (err) {
        console.log(err)
        setResultContent(modalMsg.error)
        setShowResult(true)
      }
      setIsLoading(false)
    } else if (paymentMethod === 'stripe') {
      // Stripe payment (deshabilitado pero mantiene funcionalidad)
      setPaymentView(true)
    }
  }

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }

  return (
    <>
      {resultContent && (
        <ResultModal
          {...resultContent}
          isLoading={isLoading}
          open={showResult}
          setOpen={(value) => {
            setShowResult(value)
            if (!value) {
              setResultContent(null)
              setOpen(false)
              // Reset form cuando se cierra
              setAmount('')
              setConcept('')
              setPaymentDate(new Date().toISOString().split('T')[0])
              setPaymentView(false)
              setPaymentMethod(userInfo?.isAdmin ? 'manual' : 'stripe')
            }
          }}
        />
      )}
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={setOpen}
          onClick={() => {
            setOpen(false)
            setPaymentView(false)
            setShowResult(false)
            setResultContent(null)
            setAmount('')
            setConcept('')
            setPaymentDate(new Date().toISOString().split('T')[0])
            setPaymentMethod(userInfo?.isAdmin ? 'manual' : 'stripe')
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                  <div className="">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-50">
                      <CurrencyDollarIcon
                        className="h-8 w-8 text-yellow-400"
                        aria-hidden="true"
                      />
                    </div>
                    {/* <span
                    className="block text-bg mb-4 text-center font-bold text-gray-700"
                  >
                    {client.userId}
                  </span> */}
                    {paymentView && paymentMethod === 'stripe' ? (
                      <Payment
                        setTopUps={setTopUps}
                        amount={amount}
                        currency={currency}
                      />
                    ) : (
                      <>
                        {/* Cliente (solo admin) */}
                        {userInfo?.isAdmin && (
                          <div className="mt-3">
                            <label
                              htmlFor="topup-user"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Cliente
                            </label>
                            <select
                              id="topup-user"
                              value={selectedUserId || ''}
                              onChange={(e) => {
                                setSelectedUserId?.(e.target.value)
                                setPaymentView(false)
                                setAmount('')
                                setErrors(defaultErrors)
                                setExistErrorsResult(true)
                              }}
                              className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="" disabled>
                                Selecciona un cliente
                              </option>
                              {users.map((user) => (
                                <option key={user.userId} value={user.userId}>
                                  {user.username || user.email || user.userId}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        {/* Método de pago */}
                        <div className="mt-3">
                          <label
                            htmlFor="payment-method"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Método de pago
                          </label>
                          <select
                            id="payment-method"
                            value={paymentMethod}
                            onChange={(e) => {
                              setPaymentMethod(e.target.value)
                              setPaymentView(false)
                            }}
                            className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                          >
                            {userInfo?.isAdmin && (
                              <option value="manual">
                                Añadir automáticamente
                              </option>
                            )}
                            <option value="stripe" disabled>
                              Stripe (No disponible)
                            </option>
                          </select>
                          {paymentMethod === 'stripe' && (
                            <p className="mt-1 text-xs text-gray-500 italic">
                              Este método de pago no está disponible actualmente
                            </p>
                          )}
                        </div>

                        {/* Campos adicionales para método manual (solo admin) */}
                        {paymentMethod === 'manual' && userInfo?.isAdmin && (
                          <>
                            <div className="mt-3">
                              <label
                                htmlFor="concept"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Concepto
                              </label>
                              <input
                                type="text"
                                name="concept"
                                id="concept"
                                value={concept}
                                onChange={(e) => setConcept(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="Concepto de la recarga"
                              />
                            </div>
                            <div className="mt-3">
                              <label
                                htmlFor="payment-date"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Fecha de pago
                              </label>
                              <input
                                type="date"
                                name="payment-date"
                                id="payment-date"
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-3 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                              />
                            </div>
                          </>
                        )}

                        <div className="mt-3">
                          <label
                            htmlFor="price"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Saldo actual
                          </label>
                          <div className="relative mt-1 w-52 rounded-md shadow-sm">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                              <span className="text-gray-500 sm:text-sm">
                                {currencies[currency].symbol}
                              </span>
                            </div>
                            <input
                              type="text"
                              name="price"
                              id="price"
                              value={client?.balance ?? ''}
                              step="0.01"
                              disabled
                              className="block w-full rounded-md border-gray-300 pl-7 py-2 pr-12 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              placeholder="0.00"
                              aria-describedby="price-currency"
                            />
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                              <span
                                className="text-gray-500 sm:text-sm"
                                id="price-currency"
                              >
                                {currencies[currency].code}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3">
                          <label
                            htmlFor="price"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Saldo a añadir
                          </label>
                          <div className="relative mt-1 w-52 rounded-md shadow-sm">
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                              <span className="text-gray-500 sm:text-sm">
                                {currencies[currency].symbol}
                              </span>
                            </div>
                            <input
                              type="text"
                              name="add-price"
                              id="add-price"
                              step="0.01"
                              className={
                                existErrorsResult
                                  ? 'block w-full rounded-md pl-7 py-2 pr-12 bg-gray-200 text-gray-700 border border-red-500 focus:outline-none focus:bg-white'
                                  : 'block w-full rounded-md border-gray-300 pl-7 py-2 pr-12 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'
                              }
                              placeholder="0.00"
                              aria-describedby="price-currency"
                              onChange={checkNumber}
                            />
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                              <span
                                className="text-gray-500 sm:text-sm"
                                id="price-currency"
                              >
                                {currencies[currency].code}
                              </span>
                            </div>
                          </div>
                          {Object.keys(errors).map((error) => {
                            if (errors[error]) {
                              return (
                                <p
                                  key={error}
                                  className="text-red-500 text-xs italic"
                                >
                                  {errorText[error]}
                                </p>
                              )
                            }
                            return null
                          })}
                          <div className="text-right sm:col-span-2 mt-4">
                            <button
                              type="submit"
                              disabled={
                                existErrorsResult ||
                                paymentMethod === 'stripe' ||
                                (userInfo?.isAdmin && !selectedUserId)
                              }
                              onClick={topUp}
                              className={
                                (!existErrorsResult &&
                                paymentMethod !== 'stripe'
                                  ? ''
                                  : 'opacity-50 cursor-not-allowed') +
                                'inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                              }
                            >
                              Top Up
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default AddMoneyModal
